import React, { FC } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import 'styles/main.scss';
import './LandingPage.scss';
import LandingDisclaimers from 'components/LandingDisclaimers';
import LandingHeader from 'components/LandingHeader';
import LandingBanner from 'components/LandingBanner';
import LandingBannerAlternative from 'components/LandingBannerAlternative';
import LandingOnTheGo from 'components/LandingOnTheGo';
import LandingVideo from 'components/LandingVideo';
import LandingHow from 'components/LandingHow';
import LandingMore from 'components/LandingMore';
import Footer from 'components/Footer';
import LandingFeatures from 'components/LandingFeatures';
import LandingLayout from 'components/LandingLayout';
import DangerouslySetInnerHtml from 'components/common/DangerouslySetInnerHtml';
import { Container, Row } from 'react-bootstrap';
import { ILandingPage, ProductBlockType, TAnchorWrapperProps } from './models.d';
import BannerWithHistory from 'components/common/BannerWithHistory';
import StoryTelling from 'components/StoryTelling';
import { StoryTellingType } from 'components/StoryTelling/models';
import extractUrlFromMultiPicker from 'utils/urlMultiPicker';
import getDropDownItem from 'utils/dropDownControl';
import classNames from 'classnames';
import FeaturesList from 'components/common/FeaturesList';
import NewLandingMore from 'components/NewLandingMore';

const AnchorWrapper: FC<TAnchorWrapperProps> = ({ anchor, children }) =>
  anchor ? <div id={anchor}>{children}</div> : <>{children}</>;

const COMPONENTS = {
  landingHeader: 'Landing Header',
  footer: 'Footer',
  how: 'How',
  banner: 'Banner',
  more: 'More',
  newMore: 'New More',
  features: 'Features',
  bannerAlternative: 'Banner Alternative',
  onTheGo: 'On The Go',
  video: 'Video',
  textBlock: 'Text Block',
  bannerWithHistory: 'BannerWithHistory',
  story: 'Story Telling',
  featuresList: 'Features List',
};

const PAGES_WITHOUT_TOP_PADDING = ['/spread-the-love-not-the-germs/'];

const LandingPage: FC<ILandingPage> = ({ data }) => {
  const withLogoAndFooter = PAGES_WITHOUT_TOP_PADDING.includes(data.page.link);
  const pageClasses = classNames('landing-content', {
    'landing-content--no-top-padding': PAGES_WITHOUT_TOP_PADDING.includes(data.page.link),
  });

  // const { regionDropDown, brandSettings }: TStaticProps = useStaticQuery(graphql`
  //   {
  //     regionDropDown {
  //       id
  //       selectedCountryCode
  //       section {
  //         properties {
  //           title
  //           countries {
  //             properties {
  //               code
  //               name
  //               linkURL {
  //                 url
  //                 name
  //               }
  //             }
  //           }
  //         }
  //       }
  //     }
  //     brandSettings {
  //       brandName
  //       brandLogo {
  //         fallbackUrl
  //         fluid {
  //           srcSet
  //           base64
  //         }
  //       }
  //       brandLogoSchema
  //       youtubeUrl
  //       facebookUrl
  //       schemaLogoFileName
  //       defaultOgImage
  //       contactPoint {
  //         properties {
  //           telephone
  //           contactType
  //           areaServed
  //           availableLanguage
  //           streetAddress
  //           addressRegion
  //           postalCode
  //           addressCountry
  //           foundingDate
  //         }
  //       }
  //     }
  //   }
  // `);

  return (
    <LandingLayout
      seo={{
        seoNoIndex: data.page.seoNoIndex,
        seoMetaTitle: data.page.seoMetaTitle,
        seoMetaDescription: data.page.seoMetaDescription,
        seoMetaKeywords: data.page.seoMetaKeywords,
        seoCanonicalUrl: data.page.seoCanonicalUrl,
        seoExternalHreflangs: data.page.seoExternalHreflangs,
        seoImage: data.page.seoImage,
        ogPageType: data.page.ogPageType,
      }}
      siteSettings={data.siteSettings}
      url={data.page.link}
    >
      {data.page.landingPageBody?.map((item, idx) => {
        switch (item.structure) {
          case COMPONENTS.landingHeader:
            return <LandingHeader key={idx} {...item} />;

          default: {
            return null;
          }
        }
      })}
      <div className={pageClasses}>
        {withLogoAndFooter && (
          <>
            <a className="wiggles-banner__back-button" href="/">
              <div className="icon-back button-back"></div>
              Back to
              <br />
              Dettol
            </a>
            <strong className="dt-logo landing-banner__logo">
              <a href="/">
                <img width="100%" height="100%" src="/images/Dettol_new_logo.svg" alt="Dettol AU" />
              </a>
            </strong>
          </>
        )}
        {data.page.landingPageBody?.map((item, idx) => {
          switch (item.structure) {
            case COMPONENTS.footer:
              return <LandingDisclaimers key={idx} {...item} />;

            case COMPONENTS.how:
              return <LandingHow {...item} key={idx} />;

            case COMPONENTS.banner:
              return <LandingBanner {...item} key={idx} />;

            case COMPONENTS.more:
              return <LandingMore {...item} key={idx} />;

            case COMPONENTS.newMore:
              return <NewLandingMore {...item} key={idx} />;

            case COMPONENTS.features:
              return <LandingFeatures {...item} key={idx} />;

            case COMPONENTS.bannerAlternative:
              return <LandingBannerAlternative {...item} key={idx} />;

            case COMPONENTS.onTheGo:
              return <LandingOnTheGo {...item} key={idx} />;

            case COMPONENTS.video:
              return <LandingVideo {...item} key={idx} />;

            case COMPONENTS.bannerWithHistory:
              return <BannerWithHistory {...item} key={idx} />;

            case COMPONENTS.story: {
              return (
                <AnchorWrapper
                  anchor={item.anchorTitle.toLowerCase()}
                  key={`STORY_TELLING_BLOCK-${item.anchorTitle}`}
                >
                  <StoryTelling
                    img={item.image}
                    imageAlt={item.image.altText}
                    animation={item.animation}
                    autoPlay={item.autoPlay}
                    title={{
                      regularText: item.titleRegular,
                      boldText: item.titleBold,
                    }}
                    text={item.descriptionText}
                    expansionText={item.expansionText}
                    btn={{
                      btnText: item.buttonText,
                      btnLink: extractUrlFromMultiPicker(item.buttonLink),
                      ariaLabel: item.ariaLabel,
                    }}
                    swap={getDropDownItem(item.blockType) === ProductBlockType.Left}
                    variant={getDropDownItem(item.backgroundType) as StoryTellingType}
                    rounded={getDropDownItem(item.imageType) === 'Rounded'}
                    sectionLandmark={item.sectionLandmark}
                  />
                </AnchorWrapper>
              );
            }

            case COMPONENTS.featuresList: {
              return <FeaturesList {...item} key={idx} />;
            }

            case COMPONENTS.textBlock: {
              return (
                <Container key={idx}>
                  <Row className="justify-content-md-center">
                    <DangerouslySetInnerHtml
                      html={item.articleSectionDescription}
                      className="dt-article-main-text"
                    />
                  </Row>
                </Container>
              );
            }

            default:
              return null;
          }
        })}
      </div>
      {withLogoAndFooter && (
        <Footer
          regionDropDown={data.regionDropDown}
          footerNavigation={data.footerNavigation}
          brandSettings={data.brandSettings}
          newsletterBanner={data.homepageSettings?.newsletterBanner}
          siteSettings={data.siteSettings}
        />
      )}
    </LandingLayout>
  );
};

export const query = graphql`
  query($link: String!, $lang: String) {
    languageSelector(lang: { eq: $lang }) {
      ...FragmentLanguageSwitcher
    }
    siteSettings(lang: { eq: $lang }) {
      ...FragmentSiteSettings
      titleArticlePagesRecommendedProducts
    }
    homepageSettings(lang: { eq: $lang }) {
      ...FragmentHomepageSettings
    }
    footerNavigation(lang: { eq: $lang }) {
      ...FragmentFooter
    }

    regionDropDown {
      id
      selectedCountryCode
      section {
        properties {
          title
          countries {
            properties {
              code
              name
              linkURL {
                url
                name
              }
            }
          }
        }
      }
    }
    brandSettings {
      brandName
      brandLogo {
        fallbackUrl
        fluid {
          srcSet
          base64
        }
      }
      brandLogoSchema
      youtubeUrl
      facebookUrl
      schemaLogoFileName
      defaultOgImage
      contactPoint {
        properties {
          telephone
          contactType
          areaServed
          availableLanguage
          streetAddress
          addressRegion
          postalCode
          addressCountry
          foundingDate
        }
      }
    }

    page: umbracoLandingPage(link: { eq: $link }, lang: { eq: $lang }) {
      seoNoIndex
      seoMetaTitle
      seoMetaDescription
      seoMetaKeywords
      seoCanonicalUrl
      seoImage
      ogPageType
      seoExternalHreflangs {
        key
        value
      }
      link
      landingPageBody {
        articleSectionDescription
        leftSubHeading
        rightSubHeading
        link {
          name
          url
          queryString
        }
        menuLinks {
          name
          url
          queryString
        }
        backgroundImage {
          altText
          fallbackUrl
          fluid {
            srcSet
            base64
          }
        }
        backgroundImageMobile {
          altText
          fallbackUrl
          fluid {
            srcSet
            base64
          }
        }
        productImageDesktop {
          altText
          fallbackUrl
          fluid {
            srcSet
            base64
          }
        }
        productImageMobile {
          altText
          fallbackUrl
          fluid {
            srcSet
            base64
          }
        }
        image {
          altText
          fallbackUrl
          fluid {
            srcSet
            base64
          }
        }
        heading
        subHeading
        structure
        instructions {
          value
        }
        disclaimers {
          value
        }

        features {
          structure
          properties {
            iconName
            description
          }
        }

        leftFeatures {
          structure
          properties {
            iconName
            description
          }
        }

        rightFeatures {
          structure
          properties {
            iconName
            description
          }
        }
        finePrint
        productImageDescription
        onTheGoHeading
        onTheGoDescription
        cssClassName
        video
        animation {
          url
          fallbackUrl
        }
        autoPlay
        titleBold
        titleRegular
        descriptionText
        expansionText
        buttonText
        ariaLabel
        anchorTitle
        buttonLink {
          icon
          name
          url
        }
        backgroundType
        blockType
        imageType
        sectionLandmark
        description
        background
        textColor
        items {
          structure
          properties {
            featureTitle
            featureDescription
            expandText
            featureImage {
              altText
              fallbackUrl
              fluid {
                srcSet
                base64
              }
            }
          }
        }
      }
    }
  }
`;

export default LandingPage;
